import { HUDDLE_SPEAKER_MAX_AGE_MS, HUDDLE_SPEAKER_WINDOW_MAX_MS } from '@common/interfaces/huddles'
import { SPEAKING_LEVEL } from './huddleAudio'

/** The most history kept, and the longest a line said to Arc is looked back over. */
export const SPEAKER_WINDOW_MS = HUDDLE_SPEAKER_WINDOW_MAX_MS
/** Speaking for this many samples (~0.3s) is speaking, not a cough. */
const MIN_SPOKEN_SAMPLES = 3
/** Slack either side of a line's span, for the trip the check took to arrive. */
const CHECK_SLACK_MS = 1500
/** Long enough to answer a check about a line said a while before Arc handed it off. */
const HISTORY_MS = HUDDLE_SPEAKER_MAX_AGE_MS + HUDDLE_SPEAKER_WINDOW_MAX_MS + CHECK_SLACK_MS
const SPOKEN_MS_PER_WORD = 350
// Speech-to-text finishes a line a moment after it's said.
const CAPTION_LAG_MS = 1500
const MIN_LINE_MS = 2500

/** Roughly how long ago a just-captioned line started: its words at speaking pace. */
export const lineWindowMs = (text: string): number => {
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.min(
    SPEAKER_WINDOW_MS,
    Math.max(MIN_LINE_MS, words * SPOKEN_MS_PER_WORD + CAPTION_LAG_MS),
  )
}
const SAMPLE_MS = 100

export interface ILevelSample {
  at: number
  levels: Map<string, number>
}

/**
 * Who spoke most since `since`: each voice's level summed over the moments it was
 * speaking. Null when nobody spoke. Two people talking at once goes to the louder.
 */
export const loudestSince = (samples: ILevelSample[], since: number): string | null => {
  const energy = new Map<string, number>()
  samples
    .filter(({ at }) => at >= since)
    .forEach(({ levels }) =>
      levels.forEach((level, key) => {
        if (level >= SPEAKING_LEVEL) energy.set(key, (energy.get(key) ?? 0) + level)
      }),
    )
  let loudest: string | null = null
  energy.forEach((total, key) => {
    if (loudest === null || total > (energy.get(loudest) ?? 0)) loudest = key
  })
  return loudest
}

/** Keeps a few seconds of every voice's level, to tell who said a line to Arc. */
export class HuddleSpeakerTracker {
  private samples: ILevelSample[] = []

  private readonly timer: ReturnType<typeof setInterval>

  constructor(
    private readonly getLevels: () => Map<string, number>,
    private readonly now: () => number = Date.now,
  ) {
    this.timer = setInterval(() => this.sample(), SAMPLE_MS)
  }

  loudest(windowMs = SPEAKER_WINDOW_MS): string | null {
    return loudestSince(this.samples, this.now() - windowMs)
  }

  /** Whether `key` was talking during a line said between those ages: this browser vouching for itself. */
  hasSpoken(key: string, fromAgoMs: number, toAgoMs: number): boolean {
    const now = this.now()
    const from = now - fromAgoMs - CHECK_SLACK_MS
    const to = now - toAgoMs + CHECK_SLACK_MS
    const spoken = this.samples.filter(
      ({ at, levels }) => at >= from && at <= to && (levels.get(key) ?? 0) >= SPEAKING_LEVEL,
    )
    return spoken.length >= MIN_SPOKEN_SAMPLES
  }

  stop(): void {
    clearInterval(this.timer)
    this.samples = []
  }

  private sample(): void {
    const at = this.now()
    this.samples.push({ at, levels: this.getLevels() })
    const cutoff = at - HISTORY_MS
    while (this.samples.length && this.samples[0].at < cutoff) this.samples.shift()
  }
}
